import React, { createContext, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { gtagEvent } from 'src/utils/gtag';
import useRestApiClient from 'src/hooks/useRestApiClient';

const initialState = {
  filteredServices: [],
  isLoading: false,
  isPreloaded: false,
  filterValues: { search_title: '' },
  setFilterValues: () => Promise.resolve()
};

const ServicesContext = createContext(initialState);

export const ServicesProvider = (props) => {
  const { children, ...contextProviderProps } = props;
  const [filterValues, setFilterValues] = useState(initialState.filterValues);
  const [isPreloaded, setPreloaded] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [services, setServices] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  const client = useRestApiClient();

  const loadServices = async (force = false) => {
    if (services.length > 0 && !force) return;
    if (isLoading) return;

    try {
      setLoading(true);
      const {
        data: { message }
      } = await client.get('/services');
      setServices(message);
      setFilteredServices(message);
      setPreloaded(true);
    } catch (error) {
      setServices([]);
      setFilteredServices([]);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let filterServices = services;
    const { search_title: searchTitle } = filterValues;

    if (searchTitle) {
      gtagEvent('search', {
        search_term: searchTitle
      });

      filterServices = filterServices.filter(
        ({ description, title }) =>
          (description &&
            description.toLowerCase().indexOf(searchTitle.toLowerCase()) >
              -1) ||
          (title && title.toLowerCase().indexOf(searchTitle.toLowerCase()) > -1)
      );
    }

    setFilteredServices(filterServices);
  }, [filterValues]);

  const values = useMemo(
    () => ({
      isPreloaded,
      isLoading,
      filterValues,
      services,
      filteredServices,
      setFilterValues,
      loadServices
    }),
    [isPreloaded, isLoading, filterValues, services, filteredServices]
  );

  return (
    <ServicesContext.Provider value={values} {...contextProviderProps}>
      {children}
    </ServicesContext.Provider>
  );
};

ServicesProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default ServicesContext;
